import { service } from '@ember/service';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import { type Task } from 'ember-concurrency';
import type EntryModel from 'garaje/models/entry';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { canSignOutEntry } from 'garaje/utils/entry-sign-out-permission';

interface SignedOutColumnArgs {
  signOutTask: Task<unknown, [EntryModel, string]>;
  entry: EntryModel;
  fullDate: boolean;
  userType: string;
  timezone?: string;
}

export default class SignedOutColumn extends Component<SignedOutColumnArgs> {
  @service declare abilities: AbilitiesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  /**
   * Permission check (no state). Whether the current user is authorized to sign
   * out entries of this kind at this location.
   */
  get hasSignOutPermission(): boolean {
    const entry = this.args.entry;
    const canEdit = this.abilities.can('edit entry', entry);
    const canSignOut = this.abilities.can('sign-out entry');

    if (this.args.userType !== 'visitor') return canEdit;

    return canSignOutEntry({
      canEdit,
      canSignOut,
      featureFlags: this.featureFlags,
      location: this.state.currentLocation,
    });
  }

  /**
   * For visitor entries, render the button (possibly disabled) when the FF is on,
   * so the explanatory tooltip can surface elsewhere. For workplace/employee entries
   * OR when the FF is off, fall back to legacy behavior and hide the button entirely
   * when the user lacks permission.
   */
  get shouldShowSignOut(): boolean {
    if (!this.args.entry.signInTime) return false;
    if (this.args.userType !== 'visitor') return this.hasSignOutPermission;
    if (!this.featureFlags.isEnabled('visitors-disabled-sign-out')) {
      return this.hasSignOutPermission;
    }
    return true;
  }

  get canSignOut(): boolean {
    return this.shouldShowSignOut && this.hasSignOutPermission;
  }

  // We need to wrap this task so the loading state doesn't propagate into other rows
  internalSignOutTask = dropTask(async () => {
    await this.args.signOutTask.perform(this.args.entry, 'visitor_log');
  });
}
